import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CounterSection = () => {
    const sectionRef = useRef(null);
    const [counters, setCounters] = useState({
        students: 0,
        countries: 0,
        visa: 0,
        years: 0,
    });
    const [hasAnimated, setHasAnimated] = useState(false);

    const stats = [
        {
            icon: 'fa-user-graduate',
            value: 1000,
            suffix: '+',
            label: 'Students Guided',
            description: 'Successfully placed in top universities worldwide',
        },
        {
            icon: 'fa-globe',
            value: 5,
            suffix: '+',
            label: 'Countries',
            description: 'Australia, UK, USA, Canada, New Zealand & Europe',
        },
        {
            icon: 'fa-check-circle',
            value: 95,
            suffix: '%',
            label: 'Visa Success Rate',
            description: 'Industry-leading visa approval rate',
        },
        {
            icon: 'fa-award',
            value: 15,
            suffix: '+',
            label: 'Years Experience',
            description: 'Trusted expertise in study abroad consulting',
        },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            ScrollTrigger.create({
                trigger: sectionRef.current,
                start: 'top 80%',
                onEnter: () => {
                    if (!hasAnimated) {
                        setHasAnimated(true);
                        animateCounters();
                    }
                },
            });
        }, sectionRef);

        return () => ctx.revert();
    }, [hasAnimated]);

    const animateCounters = () => {
        const duration = 2000;
        const steps = 60;
        const interval = duration / steps;

        let step = 0;
        const timer = setInterval(() => {
            step++;
            const progress = step / steps;

            setCounters({
                students: Math.round(1000 * progress),
                countries: Math.round(5 * progress),
                visa: Math.round(95 * progress),
                years: Math.round(15 * progress),
            });

            if (step >= steps) {
                clearInterval(timer);
                setCounters({
                    students: 1000,
                    countries: 5,
                    visa: 95,
                    years: 15,
                });
            }
        }, interval);
    };

    return (
        <section
            ref={sectionRef}
            className="counter-section py-20 bg-gradient-to-br from-black via-darkGray to-black relative overflow-hidden"
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-chart-line text-primary-red text-2xl"></i>
                        <h2 className="text-4xl font-bold text-white">Our Impact in Numbers</h2>
                    </div>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                        Trusted by thousands of students for their study abroad journey
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className={`counter-animate text-center p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 transition-all duration-500 hover:bg-white/10 hover:scale-105 ${hasAnimated ? 'visible' : ''
                                }`}
                            style={{ transitionDelay: `${index * 100}ms` }}
                        >
                            {/* Icon */}
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-red to-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className={`fas ${stat.icon} text-white text-2xl`}></i>
                            </div>

                            {/* Counter */}
                            <div className="mb-2">
                                <span className="text-5xl md:text-6xl font-extrabold text-white">
                                    {counters[Object.keys(counters)[index]]}
                                </span>
                                <span className="text-3xl font-bold text-primary-red">{stat.suffix}</span>
                            </div>

                            {/* Label */}
                            <h3 className="text-xl font-semibold text-white mb-2">{stat.label}</h3>

                            {/* Description */}
                            <p className="text-gray-400 text-sm">{stat.description}</p>
                        </div>
                    ))}
                </div>

                {/* Bottom Text */}
                <div className="text-center mt-16">
                    <p className="text-gray-300 text-lg mb-6">
                        Join our growing community of successful students
                    </p>
                    <a
                        href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20start%20my%20study%20abroad%20journey."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <i className="fas fa-arrow-right"></i>
                        Be Part of Our Success
                    </a>
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-primary-red/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-red/5 rounded-full translate-x-1/2 translate-y-1/2"></div>
        </section>
    );
};

export default CounterSection;
